import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Home, Car } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const RentalBookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const bookingNumber = searchParams.get('booking');
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingNumber) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('rental_bookings')
        .select('*')
        .eq('booking_number', bookingNumber)
        .single();

      if (error) {
        console.error('Error fetching booking:', error);
      } else {
        setBooking(data);
      }
      setLoading(false);
    };

    fetchBooking();
  }, [bookingNumber]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!booking) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Booking Not Found</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                We couldn't find your booking. Please check your booking number and try again.
              </p>
              <Link to="/car-rental">
                <Button>
                  <Car className="w-4 h-4 mr-2" />
                  Back to Car Rental
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-3xl mx-auto">
          <CardHeader className="text-center border-b">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-3xl mb-2">Booking Confirmed!</CardTitle>
            <p className="text-muted-foreground">
              Your car rental has been successfully booked
            </p>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            {/* Booking Number */}
            <div className="bg-primary/10 p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-1">Booking Number</p>
              <p className="text-2xl font-bold text-primary">{booking.booking_number}</p>
            </div>

            {/* Customer Info */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{booking.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{booking.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{booking.phone}</p>
                </div>
              </div>
            </div>

            {/* Vehicle Info */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Vehicle</h3>
              <p className="font-medium">{booking.vehicle_name}</p>
            </div>

            {/* Rental Period */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Rental Period</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Pickup</p>
                  <p className="font-medium">
                    {format(new Date(booking.pickup_date), 'PPP')}
                  </p>
                  <p className="text-sm">{booking.pickup_time}</p>
                  <p className="text-sm text-muted-foreground">{booking.pickup_location}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Return</p>
                  <p className="font-medium">
                    {format(new Date(booking.return_date), 'PPP')}
                  </p>
                  <p className="text-sm">{booking.return_time}</p>
                  <p className="text-sm text-muted-foreground">{booking.return_location}</p>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-3">Price Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Rental ({booking.total_days} days)</span>
                  <span>${booking.total_days * booking.daily_rate}</span>
                </div>
                <div className="flex justify-between">
                  <span>Insurance</span>
                  <span>${booking.insurance_cost}</span>
                </div>
                <div className="flex justify-between">
                  <span>Extras</span>
                  <span>${booking.extras_cost}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-base">
                  <span>Total</span>
                  <span className="text-primary">${booking.total_price}</span>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <span className="font-medium">Payment Status</span>
              <span className="text-green-600 font-semibold capitalize">
                {booking.payment_status}
              </span>
            </div>

            {/* Confirmation Message */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm">
                <strong>Important:</strong> A confirmation email has been sent to {booking.email}.
                Please bring your driver's license and a valid ID to the pickup location.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full">
                  <Home className="w-4 h-4 mr-2" />
                  Go to Homepage
                </Button>
              </Link>
              <Link to="/car-rental" className="flex-1">
                <Button className="w-full">
                  <Car className="w-4 h-4 mr-2" />
                  Browse More Vehicles
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RentalBookingConfirmation;